import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, Clock, User, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { feedbackService } from '../services/feedbackService';

const STATUS_COLORS = {
  open: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800'
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-gray-100 text-yellow-600',
  high: 'bg-red-100 text-red-700'
};

const FeedbackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const loadFeedback = useCallback(async () => {
    try {
      const response = await feedbackService.getFeedbackById(id);
      setFeedback(response.data);
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      await feedbackService.addComment(id, newComment);
      setNewComment('');
      await loadFeedback(); // Reload to get new comment
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await feedbackService.updateFeedback(id, { status });
      await loadFeedback();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleUpdatePriority = async (priority) => {
    try {
      await feedbackService.updateFeedback(id, { priority });
      await loadFeedback();
    } catch (error) {
      console.error('Failed to update priority:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">Feedback not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/feedback')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-5 w-5" />
        Back to Feedback
      </button>

      {/* Feedback Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <select
                value={feedback.status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
                className={`px-3 py-1 text-sm font-medium rounded-full border-0 cursor-pointer ${STATUS_COLORS[feedback.status]}`}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>

              <select
                value={feedback.priority}
                onChange={(e) => handleUpdatePriority(e.target.value)}
                className={`px-3 py-1 text-sm font-medium rounded-full border-0 cursor-pointer ${PRIORITY_COLORS[feedback.priority]}`}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>

              {feedback.category && (
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-700">
                  {feedback.category}
                </span>
              )}
            </div>

            <p className="text-lg text-gray-900 mb-4">{feedback.feedback_text}</p>

            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {feedback.user_email}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatDate(feedback.created_at)}
              </span>
            </div>

            <a
              href={feedback.page_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 mt-3 text-sm text-yellow-600 hover:text-yellow-700"
            >
              <ExternalLink className="h-4 w-4" />
              {feedback.page_url}
            </a>
          </div>
        </div>

        {/* Screenshot */}
        {feedback.screenshot_url && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
              <ImageIcon className="h-4 w-4" />
              Screenshot
            </div>
            <img
              src={feedback.screenshot_url}
              alt="Feedback screenshot"
              className="w-full rounded-md border border-gray-200"
            />
          </div>
        )}
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Discussion ({feedback.comments.length})
        </h2>

        {/* Comments List */}
        <div className="space-y-4 mb-6">
          {feedback.comments.length === 0 ? (
            <p className="text-gray-500 text-sm">No comments yet. Be the first to comment!</p>
          ) : (
            feedback.comments.map((comment) => (
              <div key={comment.id} className="border-l-4 border-yellow-500 pl-4 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900">{comment.user_email}</span>
                  <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                </div>
                <p className="text-gray-700">{comment.comment_text}</p>
              </div>
            ))
          )}
        </div>

        {/* Add Comment Form */}
        <form onSubmit={handleAddComment} className="border-t pt-4">
          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
            Add a comment
          </label>
          <textarea
            id="comment"
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts, ask a question, or provide an update..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
            disabled={isSubmittingComment}
          />
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={isSubmittingComment || !newComment.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingComment ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Post Comment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackDetail;